import { appeler } from './api.js';

/**
 * LA CREATION D UNE AUTOMATISATION [contrat 28-CG].
 *
 * FACADE PROPRE : la ressource a son contrat et son cycle. `Automatisations.tsx`
 * liste par `useListePaginee` et garde son type d index a cinq champs -- ce
 * module ne l absorbe pas, il porte l ECRITURE.
 *
 * ── CE QUE LE 201 REND, ET CE QU ON EN TYPE ───────────────
 *
 * Le serveur renvoie neuf champs (VueAutomatisation). On n en type que deux :
 * l `id` parce que la navigation en depend, le `nom` parce qu il confirme la
 * saisie. Typer les sept autres serait une abstraction speculative -- meme
 * discipline que l index, « les champs de l API, pas un de plus ».
 *
 * L `id` EST L id_public, un UUID -- pas l identifiant interne. Mesure : le
 * moteur projette `id_public` sous le nom `id`, et `resoudreAutomatisation`
 * fait le chemin inverse. C est cet id que CHEMINS.editeur attend.
 *
 * ── PAS DE CODE, PAS DE DERIVATION ───────────────
 *
 * Contrairement au projet [28-BZ], une automatisation n a pas de `code` : son
 * identite publique est un UUID. L URL n est donc pas parlante, et c est
 * assume -- il n y a rien a deriver depuis le nom.
 */
export interface DemandeCreationAutomatisation {
  readonly nom: string;
  // `?: string | undefined` ET NON `?: string` : sous
  // exactOptionalPropertyTypes, le `?` rend la cle omissible mais refuse
  // la valeur undefined explicite. Or l appelant produit toujours la cle,
  // avec undefined quand la description est vide. Meme forme que
  // DemandeCreationProjet, pour la meme raison.
  readonly description?: string | undefined;
}

export interface VueAutomatisationCreee {
  readonly id: string;
  readonly nom: string;
}

export async function creerAutomatisation(
  espace: string, projet: string, d: DemandeCreationAutomatisation,
): Promise<VueAutomatisationCreee> {
  return appeler<VueAutomatisationCreee>(
    'POST', `/locataires/${encodeURIComponent(espace)}`
      + `/projets/${encodeURIComponent(projet)}/automatisations`, d);
}

/**
 * LA PUBLICATION D UNE REVISION [29-G].
 *
 * PUBLIER N EST PAS ENREGISTRER. Enregistrer ecrit un brouillon mutable ;
 * publier fige une version IMMUABLE, et le serveur exige la REVISION
 * publiee -- jamais « le brouillon courant ». C est ce qui interdit de
 * figer autre chose que ce que l utilisateur a sous les yeux.
 *
 * LA REVISION CIRCULE EN CHAINE, aucun Number() sur le trajet : la
 * colonne est un bigint. Meme regle que src/editeur/revision.ts.
 *
 * Le 201 rend six champs ; on n en type que deux -- l identifiant de la
 * version et son numero, les seuls que l ecran montre. Typer empreinte,
 * nb_noeuds, nb_connexions et nb_declencheurs serait speculatif tant
 * qu aucun rendu ne les lit. Meme discipline que la creation.
 */
export interface VersionPubliee {
  readonly version_id: string;
  readonly numero: string;
}

export async function publierAutomatisation(
  espace: string, projet: string, automatisation: string, revision: string,
): Promise<VersionPubliee> {
  return appeler<VersionPubliee>(
    'POST', `/locataires/${encodeURIComponent(espace)}`
      + `/projets/${encodeURIComponent(projet)}`
      + `/automatisations/${encodeURIComponent(automatisation)}/versions`,
    { revision });
}

/**
 * LE DEPLOIEMENT D UNE VERSION DANS UN ENVIRONNEMENT [29-G ph.3].
 *
 * DEPLOYE N EST PAS EXECUTANT. Deployer en TEST enregistre et historise le
 * choix ; RIEN ne s execute. Seul `production` materialise webhooks et
 * planifications -- d ou `webhooks` TOUJOURS VIDE en test.
 *
 * La capacite reste `automatisation.activer` : le moteur nomme ce geste
 * activer, l ecran le nomme Deployer. A reexaminer le jour ou un role devra
 * deployer en TEST sans pouvoir agir sur PRODUCTION.
 *
 * DETTE 73 -- `numero` est un NOMBRE ici et une CHAINE dans VersionPubliee.
 * Fidele aux deux reponses moteur, qui divergent sur le meme concept. On ne
 * convertit pas : une conversion ferait croire a un contrat unique.
 *
 * LE SECRET N EXISTE QU UNE FOIS. `existant` signifie IRRECUPERABLE.
 */

export interface WebhookDeploye {
  readonly cle_noeud: string;
  readonly statut: 'cree' | 'existant';
  readonly secret?: string | undefined;
}

export interface ResultatDeploiement {
  readonly version_id: string;
  readonly numero: number;
  readonly changee: boolean;
  readonly webhooks: readonly WebhookDeploye[];
}

export async function deployerVersion(
  espace: string, projet: string, automatisation: string,
  versionId: string, environnement: 'test' | 'production',
): Promise<ResultatDeploiement> {
  return appeler<ResultatDeploiement>(
    'PUT', `/locataires/${encodeURIComponent(espace)}`
      + `/projets/${encodeURIComponent(projet)}`
      + `/automatisations/${encodeURIComponent(automatisation)}/version-active`,
    { version_id: versionId, environnement });
}
/**
 * LANCER UNE EXECUTION MANUELLE [29-H].
 *
 * On n execute PAS une version choisie : on execute celle qui est DEPLOYEE
 * dans l environnement demande. C est pourquoi le geste vit en tete de
 * l ecran des versions et jamais sur une ligne de l historique.
 *
 * `cle_declencheur` n est pas transmise : le moteur resout l unique
 * declencheur de la version deployee, et REFUSE explicitement s il y en a
 * zero ou plusieurs. L interface ne choisit pas a sa place.
 *
 * `environnement` est FACULTATIF cote serveur -- absent, c est production.
 * On l envoie toujours ici : le geste est explicite a l ecran, il doit
 * l etre dans la demande.
 */
export interface ResultatLancement {
  readonly execution_id: string;
  readonly deduplique: boolean;
}
export async function lancerExecution(
  espace: string, projet: string, automatisation: string,
  environnement: 'test' | 'production',
): Promise<ResultatLancement> {
  return appeler<ResultatLancement>(
    'POST', `/locataires/${encodeURIComponent(espace)}`
      + `/projets/${encodeURIComponent(projet)}`
      + `/automatisations/${encodeURIComponent(automatisation)}/executions`,
    { environnement });
}

/**
 * ARMER ET DESARMER [29-J].
 *
 * DEUX LIBELLES, JAMAIS UN INTERRUPTEUR. Le moteur journalise deux actions
 * distinctes -- `automatisation.armee` et `automatisation.desarmee` -- pour
 * qu on puisse demander « qui a desarme la facturation ? » par une requete
 * sur `action`. L ecran dit donc ce qu il fait, il ne bascule pas.
 *
 * ARMER N EST PAS EDITER. Le moteur REFUSE `est_arme` dans le PATCH de fiche
 * (400 corps_invalide, preuve 6 d essai-automatisations) : c est un geste
 * dedie, et il remet aussi les reserves de declencheurs a NULL. Le passer
 * par la fiche aurait maquille une remise en etat de la planification.
 *
 * LE 200 REND LA VUE RELUE, PAS UN ECHO. `armer` relit par
 * `lireAutomatisation` et echoue si l objet a disparu. On type donc les
 * DEUX faits dont l affichage depend -- `etatLisible` les exige tous les
 * deux -- et pas un de plus, meme discipline que l index.
 */
export interface VueArmement {
  readonly est_arme: boolean;
  // LE CONTRAT REEL, MESURE -- PAS SUPPOSE. La premiere version de 29-J typait
  // ici un `a_version_active: boolean` qui N EXISTE PAS dans cette reponse :
  // `armer` relit par `lireAutomatisation`, dont la vue porte `version_active`
  // -- l objet, ou null. Le champ arrivait `undefined`, `etatLisible` rendait
  // « brouillon », et la carte grisait son propre bouton apres un desarmement
  // reussi. `tsc` ne pouvait rien voir : rien ne relie ce type au SQL.
  //
  // LA LISTE ET LE GESTE DIVERGENT, ET C EST VOULU. `GET .../automatisations`
  // rend `a_version_active` (booleen) : c est un modele de LECTURE synthetique.
  // Le geste rend la vue d armement DETAILLEE. Deux representations du meme
  // fait, chacune ajustee a son usage -- pas une anomalie a aligner.
  readonly version_active: { readonly id: string; readonly numero: number } | null;
}

export async function armerAutomatisation(
  espace: string, projet: string, automatisation: string, arme: boolean,
): Promise<VueArmement> {
  return appeler<VueArmement>(
    'PUT', `/locataires/${encodeURIComponent(espace)}`
      + `/projets/${encodeURIComponent(projet)}`
      + `/automatisations/${encodeURIComponent(automatisation)}/armement`,
    { arme });
}
